import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { LogOut, Upload, Trash2, ArrowLeft } from "lucide-react";
import { api, resolveImg, formatApiError } from "@/lib/api";
import ContentEditor from "@/components/ContentEditor";
import SecuritySection from "@/components/SecuritySection";
import AnalyticsSection from "@/components/AnalyticsSection";

const SECTIONS = [
  { key: "sala_pesi", label: "Sala Pesi" },
  { key: "sala_posing", label: "Sala Posing" },
  { key: "zona_relax", label: "Zona Relax" },
  { key: "brand", label: "I Nostri Brand" },
];

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("fs_admin_token", data.token);
      onLogin(data.user);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-login" className="admin-area min-h-screen flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm border border-[rgba(176,132,51,0.3)] bg-surface/70 backdrop-blur-xl p-8 flex flex-col gap-5"
      >
        <img src="/assets/logo-fs.png" alt="Full Stack" className="h-16 w-16 object-contain mx-auto" />
        <h1 className="font-display text-2xl tracking-[0.25em] text-center text-bone">AREA RISERVATA</h1>
        <input
          data-testid="admin-email-input"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className="bg-ink border border-[rgba(176,132,51,0.3)] px-4 py-3 text-bone placeholder:text-bone/30 focus:outline-none focus:border-gold transition-colors"
        />
        <input
          data-testid="admin-password-input"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="bg-ink border border-[rgba(176,132,51,0.3)] px-4 py-3 text-bone placeholder:text-bone/30 focus:outline-none focus:border-gold transition-colors"
        />
        {error && (
          <p data-testid="admin-login-error" className="text-red-400 text-sm">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={busy}
          className="bg-gold text-ink font-display tracking-[0.2em] text-lg py-3 hover:bg-gold-dark transition-colors disabled:opacity-50"
        >
          {busy ? "ACCESSO..." : "ACCEDI"}
        </button>
        <a
          data-testid="forgot-password-link"
          href="/reset-password"
          className="text-bone/40 hover:text-gold text-xs text-center tracking-widest transition-colors"
        >
          PASSWORD DIMENTICATA?
        </a>
        <a href="/" className="text-bone/40 hover:text-gold text-xs text-center tracking-widest transition-colors">
          TORNA AL SITO
        </a>
      </form>
    </div>
  );
}

function UploadButton({ section, onDone }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      if (section) form.append("section", section);
      form.append("file", file);
      await api.post(section ? "/admin/images" : "/admin/hero", form);
      toast.success("Foto caricata");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handle} />
      <button
        data-testid={`upload-${section || "hero"}-button`}
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="inline-flex items-center gap-2 border border-gold/50 text-gold px-4 py-2 text-sm font-display tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors disabled:opacity-50"
      >
        <Upload size={16} /> {busy ? "CARICAMENTO..." : "CARICA FOTO"}
      </button>
    </>
  );
}

function Manager({ user, onLogout }) {
  const [site, setSite] = useState(null);

  const load = useCallback(() => {
    api.get("/site").then((res) => setSite(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/images/${id}`);
      toast.success("Foto eliminata");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-dashboard" className="admin-area min-h-screen px-6 md:px-12 py-10 max-w-[1200px] mx-auto">
      <div className="flex items-center justify-between mb-12">
        <div className="flex items-center gap-4">
          <img src="/assets/logo-fs.png" alt="" className="h-12 w-12 object-contain" />
          <div>
            <h1 className="font-display text-2xl md:text-3xl tracking-[0.2em] text-bone">GESTIONE FOTO</h1>
            <p className="text-bone/40 text-xs">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a
            data-testid="admin-back-to-site"
            href="/"
            className="inline-flex items-center gap-2 text-bone/50 hover:text-gold text-sm transition-colors"
          >
            <ArrowLeft size={16} /> Sito
          </a>
          <button
            data-testid="admin-logout-button"
            onClick={onLogout}
            className="inline-flex items-center gap-2 border border-[rgba(176,132,51,0.4)] text-bone/70 px-4 py-2 text-sm hover:border-gold hover:text-gold transition-colors"
          >
            <LogOut size={16} /> Esci
          </button>
        </div>
      </div>

      <AnalyticsSection />

      <section data-testid="admin-hero-section" className="border border-[rgba(176,132,51,0.25)] p-6 mb-10">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl tracking-[0.2em] text-gold">SFONDO HOME (HERO)</h2>
          <UploadButton section={null} onDone={load} />
        </div>
        {site?.hero_url ? (
          <img
            src={resolveImg(site.hero_url)}
            alt="Sfondo home attuale"
            className="w-full max-h-64 object-cover border border-[rgba(176,132,51,0.2)]"
          />
        ) : (
          <p className="text-bone/40 text-sm">Nessuna immagine impostata.</p>
        )}
        <p className="text-bone/30 text-xs mt-3">
          Carica qui la foto della palestra: apparirà dietro il logo nella schermata home. Puoi sostituirla quando
          vuoi caricandone una nuova.
        </p>
      </section>

      <ContentEditor content={site?.content} onSaved={load} />

      <SecuritySection />

      {SECTIONS.map((sec) => (
        <section
          key={sec.key}
          data-testid={`admin-section-${sec.key}`}
          className="border border-[rgba(176,132,51,0.25)] p-6 mb-10"
        >
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-xl tracking-[0.2em] text-gold">{sec.label.toUpperCase()}</h2>
            <UploadButton section={sec.key} onDone={load} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(site?.galleries?.[sec.key] || []).map((img) => (
              <div key={img.id} className="relative group border border-[rgba(176,132,51,0.2)]">
                <img src={resolveImg(img.url)} alt="" className="w-full h-36 object-cover" />
                <button
                  data-testid={`delete-${sec.key}-${img.id}`}
                  onClick={() => remove(img.id)}
                  aria-label="Elimina foto"
                  className="absolute top-2 right-2 bg-ink/80 border border-red-500/50 text-red-400 p-2 opacity-0 group-hover:opacity-100 hover:bg-red-500 hover:text-white transition-all"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {(site?.galleries?.[sec.key] || []).length === 0 && (
              <p className="text-bone/40 text-sm col-span-full">Nessuna foto: caricane una con il pulsante qui sopra.</p>
            )}
          </div>
        </section>
      ))}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    localStorage.removeItem("fs_admin_token");
    setUser(false);
  };

  if (user === null) {
    return <div className="min-h-screen bg-ink" />;
  }
  if (user === false) {
    return <LoginForm onLogin={setUser} />;
  }
  return <Manager user={user} onLogout={logout} />;
}
